<!-- Cost Analysis Display Component -->
@if (costAnalysis) {
  <div class="cost-analysis-display" [class]="'display-mode-' + displayMode">
    <!-- Summary Mode: Compact cost information -->
    @if (displayMode === 'summary') {
      <div class="cost-summary">
        <div class="cost-main-info">
          <mat-icon class="cost-icon" [class]="efficiencyColorClass">{{ efficiencyIcon }}</mat-icon>
          <span class="cost-text">{{ costSummary }}</span>
        </div>
        @if (multiModel) {
          <mat-chip class="multi-model-chip" matTooltip="Multiple AI models used">
            {{ modelCosts.length }} models
          </mat-chip>
        }
      </div>
    }
    <!-- Compact Mode: Single line with essential info -->
    @if (displayMode === 'compact') {
      <div class="cost-compact">
        <mat-icon class="cost-icon-small" [class]="efficiencyColorClass">{{ efficiencyIcon }}</mat-icon>
        <span class="cost-amount">{{ formatCost(costAnalysis.totalCostUsd || 0) }}</span>
        <span class="token-count">{{ formatTokens(costAnalysis.totalTokens || 0) }} tokens</span>
      </div>
    }
    <!-- Detailed Mode: Full breakdown with expansion panel -->
    @if (displayMode === 'detailed') {
      <div class="cost-detailed">
        <!-- Main Cost Summary -->
        <div class="cost-header">
          <div class="cost-main">
            <div class="cost-info">
              <div class="total-cost">{{ formatCost(costAnalysis.totalCostUsd || 0) }}</div>
              <div class="total-tokens">{{ formatTokens(costAnalysis.totalTokens || 0) }} tokens</div>
              @if (processingInfo) {
                <div class="processing-info">{{ processingInfo }}</div>
              }
            </div>
          </div>
          <div class="cost-efficiency">
            <div class="efficiency-label">
              <mat-icon class="efficiency-icon" [class]="efficiencyColorClass">{{ efficiencyIcon }}</mat-icon>
              Cost Efficiency
            </div>
            <div class="cost-per-token" [class]="efficiencyColorClass">
              {{ costPerToken }}
            </div>
          </div>
        </div>
        <!-- Model Breakdown (for multi-model tasks) -->
        @if (showBreakdown && multiModel) {
          <mat-expansion-panel class="model-breakdown-panel">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mat-icon>analytics</mat-icon>
                Per-Model Breakdown ({{ modelCosts.length }} models)
              </mat-panel-title>
            </mat-expansion-panel-header>
            <div class="model-costs-list">
              @for (modelCost of modelCosts; track modelCost.model) {
                <div class="model-cost-item">
                  <div class="model-header">
                    <div class="model-name">
                      <mat-icon>smart_toy</mat-icon>
                      {{ modelCost.model }}
                      @if (modelCost.model === costAnalysis.primaryModel) {
                        <mat-chip class="primary-model-chip"> Primary </mat-chip>
                      }
                    </div>
                    <div class="model-cost-summary">
                      <span class="model-cost">{{ formatCost(modelCost.cost.estimatedTotalCostUsd || 0) }}</span>
                      <span class="model-percentage">
                        ({{ getModelCostPercentage(modelCost.cost.estimatedTotalCostUsd || 0).toFixed(1) }}%)
                      </span>
                    </div>
                  </div>
                  <div class="model-details">
                    <div class="model-tokens unified-token-style">
                      <mat-icon>token</mat-icon>
                      {{ formatTokens(modelCost.cost.totalTokens || 0) }} tokens
                    </div>
                    @if (modelCost.breakdown.length > 0) {
                      <div class="token-breakdown-detailed">
                        <table class="token-breakdown-table compact">
                          <thead>
                            <tr>
                              <th class="token-type-header">Type</th>
                              <th class="token-count-header">Tokens</th>
                              <th class="token-cost-header">Cost</th>
                              <th class="token-rate-header">Rate/M</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (item of modelCost.breakdown; track item.label) {
                              <tr class="breakdown-row">
                                <td class="token-type-cell">
                                  <mat-icon class="token-icon">{{ item.icon }}</mat-icon>
                                  <span class="token-label">{{ item.label }}</span>
                                </td>
                                <td class="token-count-cell">{{ formatTokens(item.tokens) }}</td>
                                <td class="token-cost-cell">{{ item.cost > 0 ? formatCost(item.cost) : '—' }}</td>
                                <td class="token-rate-cell">
                                  {{ item.rate > 0 ? '$' + formatRatePerMillion(item.rate) : '—' }}
                                </td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          </mat-expansion-panel>
        }
        <!-- Single Model Details -->
        @if (showBreakdown && !multiModel && modelCosts.length > 0) {
          <div class="single-model-details">
            <div class="model-header">
              <div class="model-name">
                <mat-icon>smart_toy</mat-icon>
                {{ modelCosts[0].model }}
              </div>
              <div class="model-tokens unified-token-style">
                {{ formatTokens(modelCosts[0].cost.totalTokens || 0) }} tokens
              </div>
            </div>
            @if (modelCosts[0].breakdown.length > 0) {
              <div class="token-breakdown-detailed">
                <div class="breakdown-title">Token Usage Breakdown:</div>
                <table class="token-breakdown-table">
                  <thead>
                    <tr>
                      <th class="token-type-header">Type</th>
                      <th class="token-count-header">Tokens</th>
                      <th class="token-cost-header">Cost</th>
                      <th class="token-rate-header">Rate/M</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of modelCosts[0].breakdown; track item.label) {
                      <tr class="breakdown-row">
                        <td class="token-type-cell">
                          <mat-icon class="token-icon">{{ item.icon }}</mat-icon>
                          <span class="token-label">{{ item.label }}</span>
                        </td>
                        <td class="token-count-cell">{{ formatTokens(item.tokens) }}</td>
                        <td class="token-cost-cell">{{ item.cost > 0 ? formatCost(item.cost) : '—' }}</td>
                        <td class="token-rate-cell">
                          {{ item.rate > 0 ? '$' + formatRatePerMillion(item.rate) : '—' }}
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
}

<!-- No Cost Data Available -->
@if (!costAnalysis) {
  <div class="no-cost-data">
    <mat-icon class="no-data-icon">help_outline</mat-icon>
    <span class="no-data-text">No cost data available</span>
  </div>
}
